import { useAxios } from '../useAxios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  forgotPassword,
  getCurrentUser,
  resetPassword,
  signIn,
  signUp,
  switchTeam,
  updatePassword,
  updateUser,
  UserSignUpType,
  verify
} from '../../services/api/auth';
import { useAuth } from '../../contexts/authContext';
import useTeamStore from 'store/useTeamStore';
import { ITeam } from '@recone/models';
import { useShallow } from 'zustand/react/shallow';

export const GET_CURRENT_USER_QUERY_ID = 'client';

export function useSignIn() {
  const axios = useAxios();

  return useMutation((args: { email: string; password: string }) => signIn(axios, args.email, args.password));
}

export function useSignUp() {
  const axios = useAxios();

  return useMutation((args: { userData: UserSignUpType }) => signUp(axios, args.userData));
}

export function useVerify() {
  const axios = useAxios();
  const [teamId, setTeamId] = useTeamStore(useShallow((state) => [state.teamId, state.setTeamId]));

  return useMutation((args: { email: string; code: string }) =>
    verify(axios, args.email, args.code).then((res) => {
      if (!teamId && (res.data.teams?.[0] as ITeam)?._id) {
        setTeamId((res.data.teams?.[0] as ITeam)?._id);
      }
      return res;
    })
  );
}

export function useGetCurrentUser() {
  const axios = useAxios();
  const { token } = useAuth();
  const [teamId, setTeamId] = useTeamStore(useShallow((state) => [state.teamId, state.setTeamId]));

  return useQuery(
    [GET_CURRENT_USER_QUERY_ID],
    () =>
      getCurrentUser(axios).then((res) => {
        if (!teamId && (res.data.teams?.[0] as ITeam)?._id) {
          setTeamId((res.data.teams?.[0] as ITeam)?._id);
        }
        return res;
      }),
    {
      enabled: !!token
    }
  );
}

export function useUpdateUserProfile() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { id: string; name: string; surname: string; email: string }) =>
      updateUser(axios, args.id, args.name, args.surname, args.email),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_CURRENT_USER_QUERY_ID]).then();
      }
    }
  );
}

export function useUpdatePassword() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { clientId: string; oldPassword: string; newPassword: string; confirmPassword: string }) =>
      updatePassword(axios, args.clientId, args.oldPassword, args.newPassword, args.confirmPassword),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_CURRENT_USER_QUERY_ID]).then();
      }
    }
  );
}

export function useSignOut() {
  const auth = useAuth();

  return () => {
    auth.removeToken();
    localStorage.removeItem('teamId');
  };
}

export function getInviteId() {
  return localStorage.getItem('invitationId');
}

export function useForgotPassword() {
  const axios = useAxios();

  return useMutation((args: { email: string }) => forgotPassword(axios, args.email));
}

export function useResetPassword() {
  const axios = useAxios();

  return useMutation((args: { code: string; password: string; repeatPassword: string }) =>
    resetPassword(axios, args.code, args.password, args.repeatPassword)
  );
}

export function useSwitchTeam() {
  const axios = useAxios();

  return useMutation((args: { selectedTeamId: string }) => switchTeam(axios, args.selectedTeamId));
}
